import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Stage } from './models';
import { baseApi } from '@centerpiece/components';

export const stage = baseApi
  .enhanceEndpoints({ addTagTypes: ['Stages', 'Stage', 'Project'] })
  .injectEndpoints({
    endpoints: builder => ({
      getStages: builder.query<Stage[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'stage',
          method: 'GET',
          params: params ? params : { nested: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Stage' as const, id })),
                'Stages',
              ]
            : ['Stages'],
      }),
      getStage: builder.query<Stage, string>({
        query: id => ({
          url: `stage/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Stage', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createStage: builder.mutation({
        query: request => ({
          url: 'stage',
          method: 'POST',
          body: request,
        }),
        // invalidatesTags: ['Stages'],
        invalidatesTags: (result, error, arg) => [
          'Stages',
          { type: 'Project', id: arg.project },
        ],
      }),
      patchStage: builder.mutation<Stage, Stage>({
        query: request => ({
          url: `stage/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Stage', id: arg.id },
          { type: 'Project', id: arg.project },
        ],
      }),
      updateStage: builder.mutation<Stage, Stage>({
        query: request => ({
          url: `stage/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Stage', id: arg.id },
          { type: 'Project', id: arg.project },
        ],
      }),
      deleteStage: builder.mutation<Stage, number>({
        query: id => ({
          url: `stage/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Stage' as const, arg }, 'Stages', 'Project']
            : ['Stages'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchStageMutation,
  useGetStagesQuery,
  useGetStageQuery,
  useCreateStageMutation,
  useUpdateStageMutation,
  useDeleteStageMutation,
  useLazyGetStageQuery,
  usePrefetch: useStagesPrefetch,
} = stage;
const selectStagesRoot = stage.endpoints.getStages.select({});
export const selectStages = createSelector(
  selectStagesRoot,
  stage => stage.data || []
);

export const subscribeStage = stage.endpoints.getStage.useQueryState;
